import React, { FC } from 'react';
import { Modal, Paper, Box, IconButton, Fade, Backdrop, CircularProgress, Button, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import './coe-training-draft-modal.component.css';
import { palette } from '../../../theme';

export interface COETrainingDraftModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  gradientHeader?: boolean;
  headerLayout?: 'compact' | 'centered';
  badge?: string;
  badgeTone?: 'draft' | 'live';
  children: React.ReactNode;
  error?: string;
  loading?: boolean;
  actions: Array<{
    title: string;
    color: 'primary' | 'error' | 'secondary' | 'success' | 'info' | 'warning';
    variant: 'contained' | 'outlined' | 'text';
    onClick: () => void;
    width?: number | string;
    hide?: boolean;
  }>;
  headerIcon?: React.ReactNode;
  headerActions?: Array<{
    icon: React.ReactNode;
    onClick: () => void;
  }>;
  hideFooter?: boolean;
}

const COETrainingDraftModal: FC<COETrainingDraftModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  gradientHeader = false,
  headerLayout = 'compact',
  badge,
  badgeTone = 'draft',
  children,
  error,
  loading = false,
  actions,
  headerIcon,
  headerActions,
  hideFooter = false,
}) => {
  const isCentered = gradientHeader && headerLayout === 'centered';

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        onClick: onClose,
      }}
      disableEscapeKeyDown={false}
      className="coe-training-draft-modal-container"
    >
      <Fade in={open} timeout={500}>
        <Paper
          className={`coe-training-draft-modal-paper${gradientHeader ? ' coe-training-draft-modal-paper-flush' : ''}`}
        >
          {loading ? (
            <Box className="coe-training-draft-modal-loading">
              <CircularProgress size={60} />
            </Box>
          ) : (
            <>
              {gradientHeader ? (
                <Box
                  className={`coe-training-draft-modal-gradient-header${
                    isCentered ? ' coe-training-draft-modal-gradient-header--centered' : ''
                  }`}
                >
                  <IconButton
                    onClick={onClose}
                    className="coe-training-draft-modal-gradient-close"
                    aria-label="Close"
                    size="small"
                  >
                    <CloseIcon style={{ fontSize: 18 }} />
                  </IconButton>
                  {isCentered ? (
                    <Box className="coe-training-draft-modal-gradient-centered">
                      <h3 className="coe-training-draft-modal-gradient-title coe-training-draft-modal-gradient-title--lg">
                        {title}
                      </h3>
                      {badge ? (
                        <span
                          className={`coe-training-draft-modal-status-badge${
                            badgeTone === 'live' ? ' coe-training-draft-modal-status-badge--live' : ''
                          }`}
                        >
                          {badge}
                        </span>
                      ) : null}
                      {subtitle ? <p className="coe-training-draft-modal-gradient-subtitle">{subtitle}</p> : null}
                    </Box>
                  ) : (
                    <Box className="coe-training-draft-modal-gradient-title-block">
                      {headerIcon ? <Box className="coe-training-draft-modal-gradient-icon">{headerIcon}</Box> : null}
                      <Box>
                        <Box className="coe-training-draft-modal-gradient-title-row">
                          <h3 className="coe-training-draft-modal-gradient-title">{title}</h3>
                          {badge ? (
                            <span
                              className={`coe-training-draft-modal-status-badge coe-training-draft-modal-status-badge--inline${
                                badgeTone === 'live' ? ' coe-training-draft-modal-status-badge--live' : ''
                              }`}
                            >
                              {badge}
                            </span>
                          ) : null}
                        </Box>
                        {subtitle ? <p className="coe-training-draft-modal-gradient-subtitle">{subtitle}</p> : null}
                      </Box>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box className="coe-training-draft-modal-header">
                  <IconButton
                    onClick={onClose}
                    style={{
                      padding: 0,
                      width: 30,
                      height: 30,
                      borderRadius: 8,
                      backgroundColor: palette.grey[100],
                      gridColumn: 1,
                      gridRow: 1,
                      zIndex: 1,
                    }}
                  >
                    <CloseIcon style={{ fontSize: 16, color: palette.text.primary }} />
                  </IconButton>
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      gridColumn: '1 / -1',
                      gridRow: 1,
                    }}
                  >
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{title}</h3>
                    {headerIcon && (
                      <Box className="coe-training-draft-modal-header-title">
                        <Box className="coe-training-draft-modal-header-icon-container">{headerIcon}</Box>
                      </Box>
                    )}
                    {headerActions &&
                      headerActions.map((headerAction, index) => (
                        <IconButton onClick={() => headerAction.onClick()} key={index}>
                          {headerAction.icon}
                        </IconButton>
                      ))}
                  </Box>
                  <Box style={{ gridColumn: 3, gridRow: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    {badge && (
                      <span
                        style={{
                          border: `1px solid ${palette.divider}`,
                          borderRadius: 6,
                          padding: '3px 10px',
                          fontSize: 12,
                          fontWeight: 600,
                          color: palette.text.secondary,
                          letterSpacing: '0.04em',
                        }}
                      >
                        {badge}
                      </span>
                    )}
                  </Box>
                </Box>
              )}

              {error && (
                <Alert severity="error" className="coe-training-draft-modal-error">
                  {error}
                </Alert>
              )}

              <Box
                className={`coe-training-draft-modal-content${
                  gradientHeader ? ' coe-training-draft-modal-content-padded' : ''
                }`}
              >
                {children}
              </Box>

              {!hideFooter && (
                <Box
                  className={`coe-training-draft-modal-footer${
                    gradientHeader ? ' coe-training-draft-modal-footer--mock' : ''
                  }`}
                >
                  <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={loading}
                    className="coe-training-draft-modal-btn coe-training-draft-modal-btn--cancel"
                  >
                    Cancel
                  </Button>
                  {actions
                    .filter((action) => !action.hide)
                    .map((action, index) => (
                      <Button
                        key={index}
                        variant={action.variant}
                        color={action.color}
                        onClick={action.onClick}
                        disabled={loading}
                        className={`coe-training-draft-modal-btn ${
                          action.variant === 'outlined'
                            ? 'coe-training-draft-modal-btn--outline'
                            : 'coe-training-draft-modal-btn--primary'
                        }`}
                        style={{ width: action.width || 'auto' }}
                      >
                        {action.title}
                      </Button>
                    ))}
                </Box>
              )}
            </>
          )}
        </Paper>
      </Fade>
    </Modal>
  );
};

export default COETrainingDraftModal;
